import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import type { CargoPlanningStatusDto, CargoPlanningSyncState } from "../../dto/Applications/CargoPlanningDto";
import { initialCargoPlanningState, type CargoPlanningState } from "./cargoPlanningTypes";
import {
  areCargoPlanningDocumentsEqual,
  canOpenCargoPlanning,
  getCargoPlanningDocumentIds,
  getCargoPlanningMetadataKey,
  isCargoPlanningWaiting,
} from "./utils/cargoPlanning";
import { ApplicationFilterModel, ApplicationMatchModel, ApplicationModel } from "../../viewModels/Applications/ApplicationModel";
import ApplicationServiceModel from "../../viewModels/Applications/ApplicationServiceModel";
import BorderCrossingModel from "../../viewModels/Location/BorderCrossingModel";
import { ApplicationRecommendationsModel } from "../../viewModels/Applications/ApplicationRecommendationsModel";
import type RateModel from "../../viewModels/Rate/RateModel";
import { SalesIndicativeDto } from "../../dto/Applications/SalesIndicativeDto";
import type {
  ChangeRecommendationWish,
  RecommendationOperation,
  ResetRecommendationRoute,
  SelectRecommendationRoute,
} from "./recommendationTypes";
import {
  GetApplicationRecommendationsRequestDto,
  GetSalesIndicativePreviewRequestDto,
} from "../../api/apiServices/backOffice/controllers/applications/applications.requestsDto";
import type { AiAutofillResponseDto } from "../../api/apiServices/backOffice/controllers/applications/applications.requestsDto";
import type { CopyCalculationFromRequestDto } from "../../api/apiServices/backOffice/controllers/applications/applications.requestsDto";
import {
  AddRateInCalculationType,
  ChangeApplicationCalculationConfigurationType,
  ChangeApplicationCalculationVehicleFormInput,
  CreateCalculationSegmentType,
  RemoveRateFromCalculationType,
  UpdateRateFormInput,
  UpdateSegmentCalculationFormInput,
} from "../../components/Calculations/components/FtlCalculation";
import {
  CreateEmptyWishElementType,
  CreateWishElementByRateType,
} from "../../components/Calculations/components/FtlCalculation/components/WishListModal";

export type SalesIndicativePreviewModel = Pick<SalesIndicativeDto, "currencyId" | "totalAmount">;

export type QuotationPreviewState = {
  requestId: string;
  applicationId: number;
  calculationId: number;
  loading: boolean;
  error: boolean;
  application?: ApplicationModel;
  borderCrossings?: BorderCrossingModel[];
};

export type RecommendationQuotationCopy = RecommendationOperation & { request: CopyCalculationFromRequestDto };

export type AiFillerMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
};

export type AiFillerRequest = {
  source: "text" | "document" | "input";
  message: string;
  messageId: string;
  file?: File;
  files?: File[];
  inputText?: string;
  draft?: ApplicationModel;
};

const initialState: {
  loading: boolean;
  loadingData: boolean;
  loadingSuggestions: boolean;

  application: ApplicationModel;
  applicationSuggestions?: Array<ApplicationMatchModel>;
  recommendations?: ApplicationRecommendationsModel;
  recommendationsApplicationId?: number;
  recommendationsIncludeSimilarLocations?: boolean;
  recommendationsRequestId: number;
  loadingRecommendations: boolean;
  recommendationsError: boolean;
  recommendationRouteSelection?: RecommendationOperation;
  recommendationRouteReset?: RecommendationOperation;
  recommendationWishMutation?: RecommendationOperation;
  recommendationQuotationCopy?: RecommendationQuotationCopy;
  quotationPreview?: QuotationPreviewState;
  services?: Array<ApplicationServiceModel>;

  activeCalculationKey?: string;

  aiFillerModalOpen: boolean;
  aiFillerModalLoading: boolean;
  aiFillerMessages: Array<AiFillerMessage>;
  aiFillerLogId?: number;
  aiFillerPendingMessageId?: string;
  aiFillerError: boolean;
  aiFillerLogUpdatedAt?: number;
  borderCrossings: Array<BorderCrossingModel>;
  salesIndicative?: SalesIndicativePreviewModel;
  loadingSalesIndicative: boolean;
  salesIndicativeError: boolean;
  cargoPlanning: CargoPlanningState;
} = {
  loading: true,
  loadingData: true,
  loadingSuggestions: false,
  recommendationsRequestId: 0,
  loadingRecommendations: false,
  recommendationsError: false,

  application: undefined!,

  aiFillerModalOpen: false,
  aiFillerModalLoading: false,
  aiFillerMessages: [],
  aiFillerError: false,
  borderCrossings: undefined!,
  loadingSalesIndicative: false,
  salesIndicativeError: false,
  cargoPlanning: initialCargoPlanningState,
};

const applicationSlice = createSlice({
  name: "application",
  initialState,
  reducers: {
    loadApplication(state, _action: PayloadAction<number>) {
      state.loadingData = true;
      state.recommendations = undefined;
      state.recommendationsApplicationId = undefined;
      state.recommendationsIncludeSimilarLocations = undefined;
      state.recommendationsRequestId += 1;
      state.loadingRecommendations = false;
      state.recommendationsError = false;
      state.quotationPreview = undefined;
      state.recommendationRouteSelection = undefined;
      state.recommendationRouteReset = undefined;
      state.recommendationWishMutation = undefined;
      state.recommendationQuotationCopy = undefined;
    },
    loadApplicationSuccess(
      state,
      action: PayloadAction<{
        application: ApplicationModel;
        services?: Array<ApplicationServiceModel>;
        borderCrossings: Array<BorderCrossingModel>;
      }>,
    ) {
      const { application, services, borderCrossings } = action.payload;

      state.loading = false;
      state.loadingData = false;
      state.application = application;
      state.cargoPlanning = {
        ...initialCargoPlanningState,
        applicationId: application.id,
        dealId: application.cargoPlanningDealId ?? null,
        syncState: application.cargoPlanningSyncState ?? "NOT_STARTED",
        savedDocumentIds: getCargoPlanningDocumentIds(application.cargoInfo?.cargoDimensionsDocuments),
        applicationMetadataKey: getCargoPlanningMetadataKey(
          application.cargoPlanningDealId ?? null,
          application.cargoPlanningSyncState ?? "NOT_STARTED",
          getCargoPlanningDocumentIds(application.cargoInfo?.cargoDimensionsDocuments),
        ),
      };
      state.services = services;
      state.borderCrossings = borderCrossings;

      if (application.calculations.length > 0) {
        state.activeCalculationKey = application.calculations[0].uuid;
      }
    },
    loadSalesIndicative(state, _action: PayloadAction<GetSalesIndicativePreviewRequestDto>) {
      state.salesIndicative = undefined;
      state.loadingSalesIndicative = true;
      state.salesIndicativeError = false;
    },
    loadSalesIndicativeSuccess(state, action: PayloadAction<SalesIndicativePreviewModel>) {
      state.salesIndicative = action.payload;
      state.loadingSalesIndicative = false;
      state.salesIndicativeError = false;
    },
    loadSalesIndicativeReject(state) {
      state.salesIndicative = undefined;
      state.loadingSalesIndicative = false;
      state.salesIndicativeError = true;
    },
    invalidateSalesIndicative(state) {
      state.salesIndicative = undefined;
      state.loadingSalesIndicative = false;
      state.salesIndicativeError = false;
    },
    loadServices(
      _state,
      _action: PayloadAction<{
        filter: ApplicationFilterModel;
      }>,
    ) {},

    loadServicesSuccess(
      state,

      action: PayloadAction<Array<ApplicationServiceModel>>,
    ) {
      state.services = action.payload;
    },

    resetState: () => initialState,

    updateApplication(state) {
      state.loadingData = true;
    },

    startCargoPlanningSave(state, action: PayloadAction<number[]>) {
      state.cargoPlanning.saving = true;
      state.cargoPlanning.saveError = false;
      state.cargoPlanning.checking = false;
      state.cargoPlanning.statusError = false;
      if (
        !areCargoPlanningDocumentsEqual(action.payload, state.cargoPlanning.savedDocumentIds) ||
        state.cargoPlanning.syncState === "NOT_STARTED"
      ) {
        state.cargoPlanning.confirmed = false;
        state.cargoPlanning.url = undefined;
        state.cargoPlanning.modalSession += 1;
        state.cargoPlanning.loadingUrl = false;
        state.cargoPlanning.urlError = false;
      }
    },
    saveCargoPlanningSuccess(
      state,
      action: PayloadAction<{
        applicationId: number;
        dealId: number | null;
        syncState: CargoPlanningSyncState;
        documentIds: number[];
        updatedAt?: number;
      }>,
    ) {
      if (state.cargoPlanning.applicationId !== action.payload.applicationId) return;
      state.loadingData = false;
      state.cargoPlanning.saving = false;
      state.cargoPlanning.saveError = false;
      state.cargoPlanning.confirmed = false;
      if (action.payload.updatedAt && action.payload.updatedAt < state.cargoPlanning.applicationUpdatedAt) return;
      state.cargoPlanning.dealId = action.payload.dealId;
      state.cargoPlanning.syncState = action.payload.syncState;
      state.cargoPlanning.savedDocumentIds = action.payload.documentIds;
      state.cargoPlanning.applicationUpdatedAt = action.payload.updatedAt ?? state.cargoPlanning.applicationUpdatedAt;
      state.cargoPlanning.applicationMetadataKey = getCargoPlanningMetadataKey(
        action.payload.dealId,
        action.payload.syncState,
        action.payload.documentIds,
      );
      if (
        state.cargoPlanning.modalOpen &&
        action.payload.documentIds.length > 0 &&
        !action.payload.dealId &&
        action.payload.syncState === "NOT_STARTED"
      ) {
        state.cargoPlanning.checking = false;
        state.cargoPlanning.statusError = true;
      }
    },
    receiveCargoPlanningApplication(
      state,
      action: PayloadAction<{
        applicationId: number;
        dealId: number | null;
        syncState: CargoPlanningSyncState;
        documentIds: number[];
        updatedAt: number;
      }>,
    ) {
      const planning = state.cargoPlanning;
      const { applicationId, dealId, syncState, documentIds, updatedAt } = action.payload;
      if (planning.applicationId !== applicationId || (updatedAt && updatedAt < planning.applicationUpdatedAt)) return;
      planning.applicationUpdatedAt = updatedAt || planning.applicationUpdatedAt;
      const metadataKey = getCargoPlanningMetadataKey(dealId, syncState, documentIds);
      if (planning.applicationMetadataKey === metadataKey) return;
      planning.applicationMetadataKey = metadataKey;
      planning.savedDocumentIds = documentIds;
      // Long-poll is an invalidation signal; the status endpoint owns current integration metadata.
      planning.confirmed = false;
      planning.checking = true;
      planning.statusError = false;
      planning.url = undefined;
      planning.loadingUrl = false;
      planning.urlError = false;
      planning.modalSession += 1;
      planning.refreshVersion += 1;
    },
    updateApplicationReject(state, action: PayloadAction<number>) {
      if (state.application?.id !== action.payload) return;
      state.loadingData = false;
      state.cargoPlanning.saving = false;
      state.cargoPlanning.saveError = true;
      state.cargoPlanning.confirmed = false;
    },
    checkCargoPlanning(state) {
      state.cargoPlanning.checking = true;
      state.cargoPlanning.statusError = false;
      state.cargoPlanning.confirmed = false;
    },
    checkCargoPlanningSuccess(state, action: PayloadAction<{ applicationId: number; status: CargoPlanningStatusDto }>) {
      if (state.cargoPlanning.applicationId !== action.payload.applicationId) return;
      Object.assign(state.cargoPlanning, action.payload.status);
      state.cargoPlanning.checking = false;
      state.cargoPlanning.statusError = false;
      state.cargoPlanning.confirmed = canOpenCargoPlanning(action.payload.status);
      if (action.payload.status.dealId && !isCargoPlanningWaiting(action.payload.status) && !state.cargoPlanning.confirmed) {
        state.cargoPlanning.statusError = true;
      }
    },
    checkCargoPlanningReject(state, action: PayloadAction<number>) {
      if (state.cargoPlanning.applicationId !== action.payload) return;
      state.cargoPlanning.checking = false;
      state.cargoPlanning.statusError = true;
      state.cargoPlanning.confirmed = false;
    },
    stopCargoPlanning() {},
    changeCargoPlanningUploads(state, action: PayloadAction<{ applicationId: number; delta: number }>) {
      if (state.cargoPlanning.applicationId !== action.payload.applicationId) return;
      state.cargoPlanning.uploadingFiles = Math.max(0, state.cargoPlanning.uploadingFiles + action.payload.delta);
    },
    openCargoPlanning(state) {
      if (!state.cargoPlanning.saving) state.cargoPlanning.saveError = false;
      state.cargoPlanning.modalOpen = true;
      state.cargoPlanning.modalSession += 1;
      state.cargoPlanning.url = undefined;
      state.cargoPlanning.urlError = false;
      state.cargoPlanning.loadingUrl = false;
    },
    closeCargoPlanning(state) {
      state.cargoPlanning.modalOpen = false;
      state.cargoPlanning.modalSession += 1;
      state.cargoPlanning.url = undefined;
      state.cargoPlanning.urlError = false;
      state.cargoPlanning.loadingUrl = false;
    },
    loadCargoPlanningLink(state, _action: PayloadAction<{ applicationId: number; session: number }>) {
      state.cargoPlanning.loadingUrl = true;
      state.cargoPlanning.urlError = false;
    },
    loadCargoPlanningLinkSuccess(state, action: PayloadAction<{ applicationId: number; session: number; url: string }>) {
      if (
        state.cargoPlanning.applicationId !== action.payload.applicationId ||
        !state.cargoPlanning.modalOpen ||
        state.cargoPlanning.modalSession !== action.payload.session
      )
        return;
      state.cargoPlanning.loadingUrl = false;
      state.cargoPlanning.url = action.payload.url;
    },
    loadCargoPlanningLinkReject(state, action: PayloadAction<{ applicationId: number; session: number }>) {
      if (
        state.cargoPlanning.applicationId !== action.payload.applicationId ||
        !state.cargoPlanning.modalOpen ||
        state.cargoPlanning.modalSession !== action.payload.session
      )
        return;
      state.cargoPlanning.loadingUrl = false;
      state.cargoPlanning.urlError = true;
    },

    updateApplicationAsOffer(state, _action: PayloadAction<string | undefined>) {
      state.loadingData = true;
    },

    loadApplicationSuggestions(state, _action: PayloadAction<number>) {
      state.loadingSuggestions = true;
    },
    loadApplicationSuggestionsSuccess(state, action: PayloadAction<Array<ApplicationMatchModel>>) {
      state.loadingSuggestions = false;
      state.applicationSuggestions = action.payload;
    },
    loadApplicationSuggestionsReject(state) {
      state.loadingSuggestions = false;
      state.applicationSuggestions = undefined;
    },

    loadRecommendations(state, action: PayloadAction<Omit<GetApplicationRecommendationsRequestDto, "abortController">>) {
      const { applicationId, includeSimilarLocations } = action.payload;
      if (
        state.application?.id !== applicationId ||
        (state.recommendationsApplicationId === applicationId &&
          state.recommendationsIncludeSimilarLocations === includeSimilarLocations &&
          (state.recommendations || state.loadingRecommendations))
      ) {
        return;
      }

      state.recommendationsApplicationId = applicationId;
      state.recommendationsIncludeSimilarLocations = includeSimilarLocations;
      state.recommendationsRequestId += 1;
      state.recommendations = undefined;
      state.loadingRecommendations = true;
      state.recommendationsError = false;
    },
    loadRecommendationsSuccess(
      state,
      action: PayloadAction<{ applicationId: number; requestId: number; recommendations: ApplicationRecommendationsModel }>,
    ) {
      if (state.application?.id !== action.payload.applicationId || state.recommendationsRequestId !== action.payload.requestId) return;
      state.recommendations = action.payload.recommendations;
      state.loadingRecommendations = false;
      state.recommendationsError = false;
    },
    loadRecommendationsReject(state, action: PayloadAction<{ applicationId: number; requestId: number }>) {
      if (state.application?.id !== action.payload.applicationId || state.recommendationsRequestId !== action.payload.requestId) return;
      state.loadingRecommendations = false;
      state.recommendationsError = true;
    },

    loadQuotationPreview(state, action: PayloadAction<Pick<QuotationPreviewState, "requestId" | "applicationId" | "calculationId">>) {
      state.quotationPreview = { ...action.payload, loading: true, error: false };
    },
    loadQuotationPreviewSuccess(
      state,
      action: PayloadAction<{ requestId: string; application: ApplicationModel; borderCrossings: BorderCrossingModel[] }>,
    ) {
      if (state.quotationPreview?.requestId !== action.payload.requestId) return;
      state.quotationPreview.application = action.payload.application;
      state.quotationPreview.borderCrossings = action.payload.borderCrossings;
      state.quotationPreview.loading = false;
      state.quotationPreview.error = false;
    },
    loadQuotationPreviewReject(state, action: PayloadAction<string>) {
      if (state.quotationPreview?.requestId !== action.payload) return;
      state.quotationPreview.loading = false;
      state.quotationPreview.error = true;
    },
    closeQuotationPreview(state, action: PayloadAction<string>) {
      if (state.quotationPreview?.requestId === action.payload) state.quotationPreview = undefined;
    },

    updateApplicationSuccess(state, action: PayloadAction<ApplicationModel>) {
      state.loadingData = false;
      if (state.application?.id === action.payload.id && state.application.version > action.payload.version) return;
      state.application = action.payload;
    },

    makeCommercialOffer(state, _action: PayloadAction<number>) {
      state.loadingData = true;
    },

    makeCommercialOfferReject(state) {
      state.loadingData = false;
    },

    changeApplication(state, action: PayloadAction<ApplicationModel>) {
      state.application = action.payload;
    },

    setActiveCalulationKey(state, action: PayloadAction<string | undefined>) {
      state.activeCalculationKey = action.payload;
    },

    returnApplicationToManager(state, _action: PayloadAction<string>) {
      state.loadingData = true;
    },

    changeApplicationCalculationConfiguration(state, _action: PayloadAction<ChangeApplicationCalculationConfigurationType>) {
      state.loadingData = true;
    },
    copyRecommendationQuotation(state, action: PayloadAction<CopyCalculationFromRequestDto>) {
      if (
        state.recommendationQuotationCopy?.status === "pending" ||
        state.application?.id !== action.payload.targetApplicationId ||
        !state.application.calculations.some((calculation) => calculation.id === action.payload.targetCalculationId)
      )
        return;
      state.recommendationQuotationCopy = { targetId: action.payload.targetCalculationId, request: action.payload, status: "pending" };
      state.recommendationRouteSelection = undefined;
      state.recommendationRouteReset = undefined;
    },
    copyRecommendationQuotationSuccess(state, action: PayloadAction<{ requestId: string; application: ApplicationModel }>) {
      const operation = state.recommendationQuotationCopy;
      if (operation?.request.requestId !== action.payload.requestId || state.application?.id !== action.payload.application.id) return;
      if (action.payload.application.version >= state.application.version) {
        state.application = action.payload.application;
      }
      operation.status = "success";
      state.recommendationWishMutation = undefined;
    },
    copyRecommendationQuotationReject(state, action: PayloadAction<string>) {
      if (state.recommendationQuotationCopy?.request.requestId !== action.payload) return;
      state.recommendationQuotationCopy.status = "error";
    },
    selectRecommendationRoute(state, action: PayloadAction<SelectRecommendationRoute>) {
      state.recommendationRouteSelection = { targetId: action.payload.configuration.id, status: "pending" };
      state.recommendationRouteReset = undefined;
      state.recommendationQuotationCopy = undefined;
    },
    selectRecommendationRouteSuccess(state, action: PayloadAction<{ targetId: number; application: ApplicationModel }>) {
      if (state.application?.id !== action.payload.application.id) return;
      if (action.payload.application.version >= state.application.version) state.application = action.payload.application;
      state.recommendationRouteSelection = { targetId: action.payload.targetId, status: "success" };
    },
    selectRecommendationRouteReject(
      state,
      action: PayloadAction<{ applicationId: number; targetId: number; application?: ApplicationModel }>,
    ) {
      if (state.application?.id !== action.payload.applicationId) return;
      if (action.payload.application && action.payload.application.version >= state.application.version)
        state.application = action.payload.application;
      state.recommendationRouteSelection = { targetId: action.payload.targetId, status: "error" };
    },
    resetRecommendationRoute(state, action: PayloadAction<ResetRecommendationRoute>) {
      state.recommendationRouteReset = { targetId: action.payload.calculationId, status: "pending" };
      state.recommendationRouteSelection = undefined;
      state.recommendationQuotationCopy = undefined;
    },
    resetRecommendationRouteSuccess(state, action: PayloadAction<{ targetId: number; application: ApplicationModel }>) {
      if (state.application?.id !== action.payload.application.id) return;
      if (action.payload.application.version >= state.application.version) state.application = action.payload.application;
      state.recommendationRouteReset = { targetId: action.payload.targetId, status: "success" };
      state.recommendationWishMutation = undefined;
    },
    resetRecommendationRouteReject(
      state,
      action: PayloadAction<{ applicationId: number; targetId: number; application?: ApplicationModel }>,
    ) {
      if (state.application?.id !== action.payload.applicationId) return;
      if (action.payload.application && action.payload.application.version >= state.application.version) {
        state.application = action.payload.application;
      }
      state.recommendationRouteReset = { targetId: action.payload.targetId, status: "error" };
    },
    changeRecommendationWish(state, action: PayloadAction<ChangeRecommendationWish>) {
      state.recommendationWishMutation = { targetId: action.payload.calculationSegmentId, status: "pending" };
    },
    changeRecommendationWishSuccess(
      state,
      action: PayloadAction<{
        targetId: number;
        application: ApplicationModel;
        updatedRate?: { oldRateId: number; rate: RateModel };
      }>,
    ) {
      if (state.application?.id !== action.payload.application.id) return;
      if (action.payload.application.version >= state.application.version) state.application = action.payload.application;
      const updatedRate = action.payload.updatedRate;
      if (updatedRate && state.recommendations) {
        state.recommendations.rates.forEach((recommendation) => {
          if (recommendation.rate.id === updatedRate.oldRateId) recommendation.rate = updatedRate.rate;
        });
      }
      state.recommendationWishMutation = { targetId: action.payload.targetId, status: "success" };
    },
    changeRecommendationWishReject(
      state,
      action: PayloadAction<{ applicationId: number; targetId: number; application?: ApplicationModel }>,
    ) {
      if (state.application?.id !== action.payload.applicationId) return;
      if (action.payload.application && action.payload.application.version >= state.application.version) {
        state.application = action.payload.application;
      }
      state.recommendationWishMutation = { targetId: action.payload.targetId, status: "error" };
    },
    createCalculationSegment(state, _action: PayloadAction<CreateCalculationSegmentType>) {
      state.loadingData = true;
    },
    deleteCalculationSegment(state, _action: PayloadAction<number>) {
      state.loadingData = true;
    },
    changeApplicationCalculationVehicle(state, _action: PayloadAction<ChangeApplicationCalculationVehicleFormInput>) {
      state.loadingData = true;
    },
    updateRate(state, _action: PayloadAction<UpdateRateFormInput>) {
      state.loadingData = true;
    },
    updateCalculationSegment(state, _action: PayloadAction<UpdateSegmentCalculationFormInput>) {
      state.loadingData = true;
    },
    createWishElementByRate(state, _action: PayloadAction<CreateWishElementByRateType>) {
      state.loadingData = true;
    },
    createEmptyWishElement(state, _action: PayloadAction<CreateEmptyWishElementType>) {
      state.loadingData = true;
    },
    deleteWishElement(state, _action: PayloadAction<number>) {
      state.loadingData = true;
    },
    addRateInCalculation(state, _action: PayloadAction<AddRateInCalculationType>) {
      state.loadingData = true;
    },
    removeRateFromCalculation(state, _action: PayloadAction<RemoveRateFromCalculationType>) {
      state.loadingData = true;
    },
    clearWishList(state, _action: PayloadAction<number>) {
      state.loadingData = true;
    },

    openAiFillerModal(state) {
      state.aiFillerModalOpen = true;
    },
    closeAiFillerModal(state) {
      state.aiFillerModalOpen = false;
    },
    sendAiFillRequest(state, action: PayloadAction<AiFillerRequest>) {
      state.aiFillerModalLoading = true;
      state.aiFillerError = false;
      state.aiFillerPendingMessageId = action.payload.messageId;
      state.aiFillerMessages.push({
        id: action.payload.messageId,
        role: "user",
        text: action.payload.message,
      });
    },
    sendAiFillRequestSuccess(state, action: PayloadAction<AiAutofillResponseDto & { messageId: string; receivedAt: number }>) {
      if (state.aiFillerPendingMessageId !== action.payload.messageId) {
        return;
      }

      state.aiFillerModalLoading = false;
      state.aiFillerPendingMessageId = undefined;
      state.aiFillerLogId = action.payload.autofillLogId;
      state.aiFillerLogUpdatedAt = action.payload.receivedAt;
      state.aiFillerMessages.push({
        id: `assistant-${action.payload.autofillLogId}-${state.aiFillerMessages.length}`,
        role: "assistant",
        text: action.payload.message,
      });
    },
    sendAiFillRequestReject(state, action: PayloadAction<string>) {
      if (state.aiFillerPendingMessageId !== action.payload) {
        return;
      }

      state.aiFillerModalLoading = false;
      state.aiFillerPendingMessageId = undefined;
      state.aiFillerError = true;
    },
    resetAiFillerChat(state) {
      state.aiFillerModalLoading = false;
      state.aiFillerMessages = [];
      state.aiFillerLogId = undefined;
      state.aiFillerPendingMessageId = undefined;
      state.aiFillerLogUpdatedAt = undefined;
      state.aiFillerError = false;
    },

    startLongPoll() {},
    stopLongPoll() {},
  },
});

export default applicationSlice;
